import { FileUpload } from '@dealstack/frontend/admin/pages/common-ui/fileUpload';
import { DocumentCharacteristics } from '@dealstack/frontend/admin/pages/Manage/MIP/ManageMip/SimpleDocumentWorkflowBatch/components/documentCharacteristics';
import { InvestorSelectModal } from '@dealstack/frontend/admin/pages/Manage/MIP/ManageMip/SimpleDocumentWorkflowBatch/components/investorSelectModal';
import { investorWithFileTable as InvestorWithFileTable } from '@dealstack/frontend/admin/pages/Manage/MIP/ManageMip/SimpleDocumentWorkflowBatch/components/investorWithFileTable';
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@dealstack/frontend/shared/components/Alert/Alert';
import { Button, Text } from '@mantine/core';
import { memo } from 'react';
import styled from 'styled-components';
import { IInvestorsProps } from '@dealstack/frontend/admin/pages/Manage/MIP/ManageMip/Investors/Investors.types';
import { useSimpleDocumentWorkflowBatchController } from './controller';

const SimpleDocumentWorkflowBatch = ({
	className,
	mipId,
	dealId,
}: IInvestorsProps & { dealId: string }) => {
	const {
		isInvestorSelectModalOpen,
		investorList,
		investorListWithFiles,
		missingInvestorFiles,
		setDroppedFiles,
		onTapResetWorkflow,
		onTapCreateSimpleDocumentWorkflow,
		onTapMissingRow,
		closeInvestorSelectModal,
		onSelectInvestorInMissingRow,
		documentCharacteristicsController,
	} = useSimpleDocumentWorkflowBatchController(mipId, dealId);

	return (
		<div className={className}>
			<Text size="h2" fw={700} mb={16}>
				STEP1: Upload Documents
			</Text>
			<Text>Upload named list of documents here</Text>
			<Alert icon="information-filled" state="info" size="s">
				<AlertTitle>Remember</AlertTitle>
				<AlertDescription>
					Files are auto-matched to investors by: first name + last name (e.g. John
					Smith.pdf), email (e.g. john@example.com.pdf), or investor/user ID (e.g.
					ac4cbf52-8bdd-403d-acdd-d8d3c5c75be1.pdf). Unmatched files can be manually
					assigned.
				</AlertDescription>
			</Alert>
			{investorListWithFiles.length === 0 && (
				<FileUpload
					successCallback={(files) => {
						setDroppedFiles(files);
					}}
				/>
			)}
			{(investorListWithFiles.length > 0 || missingInvestorFiles.length > 0) && (
				<ReviewDiv>
					<Text size="h2" fw={700} mb={16}>
						STEP2: Review and Assign
					</Text>

					<DocumentCharacteristics controller={documentCharacteristicsController} />

					{investorListWithFiles.length > 0 && (
						<>
							<Text size="h2" fw={500} mb={14}>
								FOUND INVESTORS ({investorListWithFiles.length})
							</Text>
							<BottomGapDiv>
								<InvestorWithFileTable data={investorListWithFiles} />
							</BottomGapDiv>
						</>
					)}

					{missingInvestorFiles.length > 0 && (
						<>
							<Text size="h2" fw={500} mb={14}>
								MISSING INVESTORS ({missingInvestorFiles.length})
							</Text>
							<BottomGapDiv>
								<InvestorWithFileTable
									data={missingInvestorFiles}
									onClickRow={(info) => {
										onTapMissingRow(info);
									}}
								/>
							</BottomGapDiv>
						</>
					)}

					<Text size="h2" fw={700} mb={16}>
						STEP3: Send out or Restart
					</Text>

					<Button onClick={onTapCreateSimpleDocumentWorkflow} fullWidth>
						Create Simple Document Workflow
					</Button>

					<Button variant="filled" color="red" fullWidth onClick={onTapResetWorkflow}>
						Restart
					</Button>
				</ReviewDiv>
			)}
			{missingInvestorFiles.length > 0 && (
				<InvestorSelectModal
					isOpen={isInvestorSelectModalOpen}
					onClose={closeInvestorSelectModal}
					investors={investorList.sort((a, b) =>
						a.user.firstName.localeCompare(b.user.firstName)
					)}
					onSelectInvestor={(investor) => {
						onSelectInvestorInMissingRow(investor);
					}}
				/>
			)}
		</div>
	);
};

const BottomGapDiv = styled.div`
	display: flex;
	flex-direction: column;
	margin-bottom: 60px;
`;

const ReviewDiv = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
`;

const Memoized = memo(SimpleDocumentWorkflowBatch);
const Styled = styled(Memoized)`
	position: relative;

	> * {
		padding: 10px 0;
	}
`;

export { Styled as SimpleDocumentWorkflowBatch };
